import { CheckCircle2, TrendingUp, Award, ArrowRight, ChevronDown } from "lucide-react";
import { LINE_URL } from "../constants";
import { compactCases } from "../data/cases";

const CasesSection = () => {
  const cases = compactCases;

  return (
    <section data-testid="cases-section" id="cases" className="py-14 md:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-4 py-2 rounded-full text-sm font-bold mb-6">
            <Award className="w-4 h-4" />
            實際場域案例
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold text-[#0A2540] tracking-tight mb-4">
            數據見證實力
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">
            我們不只給建議，更陪您建立能賺錢的營運系統
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {cases.map((caseItem) => (
            <div key={caseItem.name} data-testid={`case-card-${caseItem.name}`}
              className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="relative h-44 md:h-56">
                <img src={caseItem.image} alt={caseItem.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs md:text-sm font-medium text-[#0A2540]">
                  {caseItem.type}
                </div>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="text-lg md:text-xl font-bold text-[#0A2540] mb-3">{caseItem.name}</h3>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {caseItem.stats.map((stat) => (
                    <div key={stat.label} className="text-center bg-[#F8F9FA] rounded-xl px-1.5 py-2 md:px-2 md:py-2.5 flex flex-col justify-center min-h-[64px] md:min-h-[72px] overflow-hidden">
                      <div className="text-sm md:text-[15px] font-black text-[#FF6B00] leading-tight whitespace-nowrap">{stat.value}</div>
                      <div className="text-[10px] md:text-[11px] text-[#475569] leading-tight mt-1 whitespace-nowrap">{stat.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-[#475569] leading-relaxed">{caseItem.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 md:mt-8 text-right">
          <p className="text-[11px] md:text-xs text-[#94a3b8] leading-relaxed">
            * 現場運營成本將依據不同場域有所變化，獲利數字僅為粗估；營收數字皆為實際案例結果
          </p>
        </div>

        {/* Expectation-building CTA */}
        <div data-testid="cases-cta" className="mt-12 md:mt-16 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A2540] via-[#0F2D52] to-[#0A2540] p-8 md:p-12 text-center shadow-2xl">
          <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#FF6B00]/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-[#F5C24A]/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-[#FF6B00]/20 text-[#FFB380] px-4 py-1.5 rounded-full text-xs md:text-sm font-bold mb-5 border border-[#FF6B00]/30">
              <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4" />
              多個成功場域，眼見為憑
            </div>
            <h3 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4">
              成功場域見證，
              <span className="block md:inline text-[#FF6B00]">歡迎 Demo 與現場查看。</span>
            </h3>
            <p className="text-sm md:text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              不用只聽數字，我們可以帶您實地看場域、跑一次 Demo 流程。<br className="hidden md:block" />
              <span className="text-white font-bold">預約諮詢，用實戰經驗解決您的住房率、提升您的營收。</span>
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a
                href={LINE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cases-cta-line"
                className="inline-flex items-center justify-center gap-2 bg-[#FF6B00] hover:bg-[#e55f00] text-white font-bold text-sm md:text-base px-7 md:px-8 py-3.5 md:py-4 rounded-full shadow-xl shadow-[#FF6B00]/30 hover:shadow-[#FF6B00]/50 transition-all hover:-translate-y-0.5 w-full sm:w-auto"
              >
                我也想成為下一個案例
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              <a
                href="#pricing"
                data-testid="cases-cta-pricing"
                className="inline-flex items-center justify-center gap-2 text-white/80 hover:text-white font-semibold text-sm md:text-base px-4 py-3 border-b border-transparent hover:border-white/50 transition-all w-full sm:w-auto"
              >
                先看合作方案
                <ChevronDown className="w-4 h-4 -rotate-90" />
              </a>
            </div>

            <div className="mt-6 md:mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs md:text-sm text-white/60">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#FF6B00]" />多個成功場域見證，可 Demo 與現場查看</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#FF6B00]" />預約諮詢解決住房率</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#FF6B00]" />專人陪跑提升營收</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CasesSection;
